import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    Chip,
    Avatar,
    Divider
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

const formatPrice = (pricingStrategy, pricingData) => {
    if (!pricingStrategy || !pricingData) return 'N/A';

    switch (pricingStrategy) {
        case 'per_guest':
            return `$${pricingData.price} per guest`;
        case 'per_hour':
            return `$${pricingData.price} per hour`;
        case 'per_day':
            return `$${pricingData.price} per day`;
        case 'per_event':
            return `$${pricingData.flatFee} flat fee`;
        case 'tiered_per_person':
            return `${pricingData.tiers?.length || 0} pricing tiers`;
        case 'per_menu_item':
            return `${pricingData.items?.length || 0} menu items`;
        case 'combo':
            return `${pricingData.packages?.length || 0} package options`;
        default:
            return `$${pricingData.price} ${pricingStrategy.replace('_', ' ')}`;
    }
};

const PackageSummaryCard = ({ packageData, onEdit }) => {
    return (
        <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {packageData.packageImage && (
                            <Avatar
                                src={packageData.packageImage}
                                variant="rounded"
                                sx={{ width: 80, height: 80 }}
                            />
                        )}
                        <Box>
                            <Typography variant="h6" component="div">
                                {packageData.packageName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {packageData.packageDescription}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Contact: {packageData.companyEmail}
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        startIcon={<EditIcon />}
                        onClick={onEdit}
                        variant="outlined"
                        size="small"
                    >
                        Edit Package
                    </Button>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Category
                        </Typography>
                        <Chip
                            label={`${packageData.category} - ${packageData.subcategory}`}
                            size="small"
                            sx={{ mt: 0.5 }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Pricing
                        </Typography>
                        <Chip
                            label={formatPrice(packageData.pricingStrategy, packageData.pricingData)}
                            size="small"
                            sx={{ mt: 0.5 }}
                        />
                    </Grid>
                </Grid>

                {packageData.pricingStrategy === 'tiered_per_person' && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Pricing Tiers
                        </Typography>
                        <Grid container spacing={1}>
                            {packageData.pricingData.tiers.map((tier, index) => (
                                <Grid item xs={12} key={index}>
                                    <Typography variant="body2">
                                        {tier.name}: ${tier.pricePerGuest}/guest ({tier.minGuests}-{tier.maxGuests} guests)
                                    </Typography>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}

                {packageData.pricingStrategy === 'per_menu_item' && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Menu Items
                        </Typography>
                        <Grid container spacing={2}>
                            {packageData.pricingData.items.map((item, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {item.image && (
                                            <Avatar
                                                src={item.image}
                                                variant="rounded"
                                                sx={{ width: 40, height: 40 }}
                                            />
                                        )}
                                        <Box>
                                            <Typography variant="body2">{item.name}</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                ${item.price}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}

                {packageData.pricingStrategy === 'combo' && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Package Options
                        </Typography>
                        {packageData.pricingData.packages.map((pkg, index) => (
                            <Box key={index} sx={{ mb: 1 }}>
                                <Typography variant="body2">
                                    {pkg.name} - ${pkg.price}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {pkg.description}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default PackageSummaryCard; 